export const GUARDIAN_RELATION_OPTIONS = [
  "Parent",
  "Father",
  "Mother",
  "Legal Guardian",
  "Grandparent",
  "Aunt",
  "Uncle",
  "Sibling",
  "Other",
] as const;

export const RoleConfig = {
  labels: {
    super_admin: "Super Admin",
    school_admin: "School Admin",
    schooladmin: "School Admin",
    principal: "Principal",
    accountant: "Accountant",
    cashier: "Cashier",
    fee_operator: "Fee Operator",
    teacher: "Teacher",
    parent: "Parent",
    student: "Student",
  },
  // Fee data visibility scope by role.
  feeDataScope: {
    schoolWideRoles: ["super_admin", "school_admin", "schooladmin", "principal"],
    selfCollectedRoles: ["accountant", "cashier", "fee_operator"],
  },
  // Row-level data scope by role, consumed by `src/lib/accessScope.ts`.
  // - sectionScoped: only see sections they are class teacher of / teach a subject in.
  // - studentScoped: only see their own linked students (applies unless the user also holds an unrestricted role).
  // - unrestricted: not narrowed by section/student (module permissions still apply).
  dataScope: {
    sectionScopedRoles: ["teacher"],
    studentScopedRoles: ["parent"],
    unrestrictedRoles: [
      "super_admin",
      "school_admin",
      "schooladmin",
      "principal",
      "accountant",
      "cashier",
      "fee_operator",
    ],
  },
} as const;

/**
 * Action-level permission inventory.
 * Maps UI action keys → roles that are allowed to perform them.
 * Role values are compared after normalisation (lowercase, alphanumeric+_ only).
 *
 * Used by `useCanDo(action)` hook in client components to show/hide buttons.
 */
export const ActionPermissions = {
  // ── Students ──────────────────────────────────────────────────────────────
  /** "+ Add Student" button on /students */
  ADD_STUDENT:        ["super_admin", "school_admin", "schooladmin"],
  /** "Edit" button on student detail / student form */
  EDIT_STUDENT:       ["super_admin", "school_admin", "schooladmin"],
  /** "Update Enrollment / Add Enrollment" button on student Classes tab */
  UPDATE_ENROLLMENT:  ["super_admin", "school_admin", "schooladmin"],

  // ── Families ──────────────────────────────────────────────────────────────
  /** "+ New Family" button on /families */
  ADD_FAMILY:         ["super_admin", "school_admin", "schooladmin"],
  /** "Edit Family" button on family detail */
  EDIT_FAMILY:        ["super_admin", "school_admin", "schooladmin"],
  /** "Add Guardian" button on family detail */
  ADD_GUARDIAN:       ["super_admin", "school_admin", "schooladmin"],

  // ── Attendance ─────────────────────────────────────────────────────────────
  /** "Mark / Save Attendance" on /attendance */
  MARK_ATTENDANCE:    ["super_admin", "school_admin", "schooladmin", "principal", "teacher"],

  // ── Fees ──────────────────────────────────────────────────────────────────
  /** "Full Ledger" link on Student → Financial tab */
  VIEW_FEE_LEDGER:    ["super_admin", "school_admin", "schooladmin", "principal", "accountant", "cashier", "fee_operator"],
  /** "Collect Payment" button on Student → Financial tab and /fees/ledger */
  COLLECT_PAYMENT:    ["super_admin", "school_admin", "schooladmin", "accountant", "cashier", "fee_operator"],
  /** Fee settings, structure, and assignment management */
  MANAGE_FEE_SETTINGS: ["super_admin", "school_admin", "schooladmin"],

  // ── Grades & Exams ────────────────────────────────────────────────────────
  /** "Save Grades" on /grades/entry */
  ENTER_GRADES:       ["super_admin", "school_admin", "schooladmin", "principal", "teacher"],
  /** Publish / regenerate report cards */
  PUBLISH_REPORT_CARD: ["super_admin", "school_admin", "schooladmin", "principal"],

  // ── Teachers / Staff ──────────────────────────────────────────────────────
  /** "+ Add Teacher" button on /teachers */
  ADD_TEACHER:        ["super_admin", "school_admin", "schooladmin"],
  /** "Edit Teacher" button on teacher detail */
  EDIT_TEACHER:       ["super_admin", "school_admin", "schooladmin"],

  // ── HR ────────────────────────────────────────────────────────────────────
  /** "+ Add Staff" on /hr/staff */
  ADD_HR_STAFF:       ["super_admin", "school_admin", "schooladmin"],
  /** HR payroll actions */
  MANAGE_PAYROLL:     ["super_admin", "school_admin", "schooladmin"],

  // ── Settings ──────────────────────────────────────────────────────────────
  /** Any settings mutation (grading scales, exam types, campuses, classes…) */
  MANAGE_SETTINGS:    ["super_admin", "school_admin", "schooladmin"],

  // ── Communication ─────────────────────────────────────────────────────────
  /** Send / publish a communication or campaign */
  SEND_COMMUNICATION: ["super_admin", "school_admin", "schooladmin", "principal", "teacher"],

  // ── Transport ─────────────────────────────────────────────────────────────
  /** Add / edit transport routes or stops */
  MANAGE_TRANSPORT:   ["super_admin", "school_admin", "schooladmin"],

  // ── Assignments ───────────────────────────────────────────────────────────
  /** Create / edit an assignment */
  MANAGE_ASSIGNMENT:  ["super_admin", "school_admin", "schooladmin", "principal", "teacher"],
} as const;

export type ActionKey = keyof typeof ActionPermissions;

const encodeSegment = (value: string | number) => encodeURIComponent(String(value));

export const UrlLinks = {
  pages: {
    home: "/",
    login: "/login",
    // Asks SecureGate for the password again (prompt=login + login_hint); see src/app/auth/reauth/route.ts.
    reauth: "/auth/reauth",
    forbidden: "/403",
    profile: "/profile",
    calendar: "/calendar",
    schools: "/schools",
    campuses: "/campuses",
    newCampus: "/campuses/new",
    editCampus: (campusId: string | number) => `/campuses/${encodeSegment(campusId)}/edit`,
    academicYears: "/acyears",
    classes: "/classes",
    blockSettings: "/settings/blocks",
    sessionSettings: "/settings/sessions",
    newClass: "/class-directory/new",
    classDetail: (classId: string | number) => `/classes/${encodeSegment(classId)}`,
    editClass: (classId: string | number) => `/classes/${encodeSegment(classId)}/edit`,
    classDirectory: "/class-directory",
    classDirectoryDetail: (classId: string | number) => `/class-directory/${encodeSegment(classId)}`,
    students: "/students",
    newStudent: "/students/new",
    studentDetail: (studentId: string | number) => `/students/${encodeSegment(studentId)}`,
    teachers: "/teachers",
    newTeacher: "/teachers/new",
    teacherDetail: (teacherId: string | number) => `/teachers/${encodeSegment(teacherId)}`,
    editTeacher: (teacherId: string | number) => `/teachers/${encodeSegment(teacherId)}/edit`,
    families: "/families",
    newFamily: "/families/new",
    familyDetail: (familyId: string | number) => `/families/${encodeSegment(familyId)}`,
    editFamily: (familyId: string | number) => `/families/${encodeSegment(familyId)}/edit`,
    editGuardian: (guardianId: string | number) => `/families/guardians/${encodeSegment(guardianId)}/edit`,
    attendance: "/attendance",
    subjects: "/subjects",
    subjectDirectory: "/subject-directory",
    subjectDirectoryDetail: (id: string | number) => `/subject-directory/${encodeSegment(id)}`,
    gradesEntry: "/grades/entry",
    gradeSubjects: "/grade-subjects",
    gradingScale: "/settings/grading-scale",
    examTypes: "/settings/exam-types",
    evaluationPeriods: "/settings/evaluation-periods",
    assessmentComponents: "/settings/assessment-components",
    developmentAreas: "/settings/development-areas",
    competencySkills: "/settings/competency-skills",
    competencyRatingScale: "/settings/competency-rating-scale",
    competencyRatings: "/academic/competency-ratings",
    holidays: "/settings/holidays",
    attendanceSettings: "/settings/attendance",
    reportCards: "/report-cards",
    reportCardDetail: (id: string | number) => `/report-cards/${encodeSegment(id)}`,
    reportCardPrint: (id: string | number) => `/report-cards/${encodeSegment(id)}/print`,
    admissionsForms: "/admissions-forms",
    adminPermissions: "/admin/permissions",
    communication: "/communication",
    reports: "/reports",
    reportsStudents: "/reports/students",
    reportsSubjectCombinations: "/reports/subject-combinations",
    reportsMarksProgress: "/reports/marks-progress",
    reportsClassReportCard: "/reports/class-report-card",
    reportsClassAttendanceRegister: "/reports/class-attendance-register",
    reportsClassReportCardPrint: (opts: {
      evaluationPeriodId: string | number;
      sectionId?: string | number;
      gradeId?: string | number;
      academicYearId?: string | number;
    }) => {
      const params = new URLSearchParams({ evaluationPeriodId: String(opts.evaluationPeriodId) });
      if (opts.sectionId) params.set("sectionId", String(opts.sectionId));
      if (opts.gradeId) params.set("gradeId", String(opts.gradeId));
      if (opts.academicYearId) params.set("academicYearId", String(opts.academicYearId));
      return `/reports/class-report-card/print?${params}`;
    },
    fees: "/fees",
    transport: "/transport",
    assignments: "/assignments",
    assignmentDetail: (id: string | number) => `/assignments/${encodeSegment(id)}`,
    applyLeave: "/leave",
    parentCalendar: "/parent-calendar",
    leaveRequests: "/leave-requests",
    parentDashboard: "/parent-dashboard",
    parentCirculars: "/parent-dashboard/circulars",
    staffInbox: "/inbox",
    parentMobileDashboard: "/parent/dashboard",
    parentMobileAttendance: "/parent/attendance",
    parentMobileFees: "/parent/fees",
    parentMobileNotifications: "/parent/notifications",
    mobileEntry: "/mobile-entry",
    hrStaff: "/hr/staff",
    hrNewStaff: "/hr/staff/new",
    hrStaffDetail: (id: string | number) => `/hr/staff/${encodeSegment(id)}`,
    hrStaffEdit: (id: string | number) => `/hr/staff/${encodeSegment(id)}/edit`,
    hrPayroll: "/hr/payroll",
    hrPayrollDetail: (id: string | number) => `/hr/payroll/${encodeSegment(id)}`,
    hrPayslipPrint: (id: string | number) => `/hr/payslips/${encodeSegment(id)}/print`,
    hrSalaryComponents: "/hr/settings/salary-components",
    feesSettings: "/fees/settings",
    feesAssignment: "/fees/assignment",
    feesCollection: "/fees/collection",
    feesReports: "/fees/reports",
    feesLedger: (studentId: string | number) => `/fees/ledger/${encodeSegment(studentId)}`,
  },
  api: {
    authPrefix: "/api/auth",
    // Starts the one fresh sign-in after a callback without the PKCE cookie (src/lib/loginRetry.ts).
    loginRetry: "/api/auth/login-retry",
    mobileToken: "/api/mobile/token",
    userProfile: "/api/userprofile",
    sessionLogout: "/api/session/logout",
    // 204 while the session is live; the middleware answers 401 + x-session-end once it has ended.
    sessionCheck: "/api/session/check",
    uploads: "/api/uploads",
    students: "/api/students",
    studentsExport: "/api/students/export",
    studentById: (studentId: string | number) => `/api/students/${encodeSegment(studentId)}`,
    studentFamily: (studentId: string | number) => `/api/students/${encodeSegment(studentId)}/family`,
    studentGuardians: (studentId: string | number) => `/api/students/${encodeSegment(studentId)}/guardians`,
    studentSiblings: (studentId: string | number) => `/api/students/${encodeSegment(studentId)}/siblings`,
    studentEnrollments: (studentId: string | number) =>
      `/api/students/${encodeSegment(studentId)}/enrollments`,
    schools: "/api/schools",
    campuses: "/api/campuses",
    campusById: (campusId: string | number) => `/api/campuses/${encodeSegment(campusId)}`,
    families: "/api/families",
    familyById: (familyId: string | number) => `/api/families/${encodeSegment(familyId)}`,
    familyGuardians: (familyId: string | number) => `/api/families/${encodeSegment(familyId)}/guardians`,
    familyStudents: (familyId: string | number) => `/api/families/${encodeSegment(familyId)}/students`,
    guardians: "/api/guardians",
    guardianById: (guardianId: string | number) => `/api/guardians/${encodeSegment(guardianId)}`,
    staff: "/api/staff",
    attendanceClasses: "/api/attendance/classes",
    attendanceSectionStudents: (sectionId: string | number) =>
      `/api/attendance/classes/${encodeSegment(sectionId)}/students`,
    attendanceStudent: (studentId: string | number) =>
      `/api/attendance/student/${encodeSegment(studentId)}`,
    attendanceNonWorkingDay: "/api/attendance/non-working-day",
    grades: "/api/grades",
    academicYears: "/api/academic-years",
    sections: "/api/sections",
    blocks: "/api/blocks",
    sessions: "/api/sessions",
    sessionEnd: (sessionId: string) => `/api/sessions/${encodeSegment(sessionId)}/end`,
    sessionsEndUser: "/api/sessions/end-user",
    blockById: (blockId: string | number) => `/api/blocks/${encodeSegment(blockId)}`,
    blocksCopy: "/api/blocks/copy",
    blocksMine: "/api/blocks/mine",
    studentGrades: "/api/student-grades",
    studentGradeById: (id: string | number) => `/api/student-grades/${encodeSegment(id)}`,
    subjects: "/api/subjects",
    subjectById: (subjectId: string | number) => `/api/subjects/${encodeSegment(subjectId)}`,
    gradeSubjects: "/api/grade-subjects",
    gradeSubjectById: (id: string | number) => `/api/grade-subjects/${encodeSegment(id)}`,
    gradeSubjectTeachers: "/api/grade-subject-teachers",
    gradingScales: "/api/grading-scales",
    gradingScaleById: (id: string | number) => `/api/grading-scales/${encodeSegment(id)}`,
    examTypes: "/api/exam-types",
    examTypeById: (id: string | number) => `/api/exam-types/${encodeSegment(id)}`,
    reportCards: "/api/report-cards",
    reportCardById: (id: string | number) => `/api/report-cards/${encodeSegment(id)}`,
    reportCardPublish: (id: string | number) => `/api/report-cards/${encodeSegment(id)}/publish`,
    reportCardRegenerate: (id: string | number) => `/api/report-cards/${encodeSegment(id)}/regenerate`,
    reportCardVersions: (id: string | number) => `/api/report-cards/${encodeSegment(id)}/versions`,
    reportCardVersionById: (id: string | number, versionId: string | number) => `/api/report-cards/${encodeSegment(id)}/versions/${encodeSegment(versionId)}`,
    reportCardsGenerate: "/api/report-cards/generate",
    sectionStudents: (id: string | number) => `/api/sections/${encodeSegment(id)}/students`,
    studentPerformance: (studentId: string | number) => `/api/students/${encodeSegment(studentId)}/performance`,
    studentAssignments: (studentId: string | number) => `/api/students/${encodeSegment(studentId)}/assignments`,
    studentSubjects: "/api/student-subjects",
    studentSubjectById: (id: string | number) => `/api/student-subjects/${encodeSegment(id)}`,
    feeCategories: "/api/fees/categories",
    feeCategoryById: (id: string | number) => `/api/fees/categories/${encodeSegment(id)}`,
    feeStructures: "/api/fees/structures",
    feeStructureById: (id: string | number) => `/api/fees/structures/${encodeSegment(id)}`,
    feeStructureDetails: (structureId: string | number) => `/api/fees/structures/${encodeSegment(structureId)}/details`,
    feeInstallmentSchedules: (structureId: string | number) => `/api/fees/structures/${encodeSegment(structureId)}/schedules`,
    feeLatePolicies: (structureId: string | number) => `/api/fees/structures/${encodeSegment(structureId)}/late-policies`,
    feeAssignments: "/api/fees/assignments",
    feeAssignmentById: (id: string | number) => `/api/fees/assignments/${encodeSegment(id)}`,
    feeGenerateInstallments: (assignmentId: string | number) => `/api/fees/assignments/${encodeSegment(assignmentId)}/generate-installments`,
    feeInstallments: "/api/fees/installments",
    feeInstallmentById: (id: string | number) => `/api/fees/installments/${encodeSegment(id)}`,
    feeConcessions: "/api/fees/concessions",
    feeConcessionById: (id: string | number) => `/api/fees/concessions/${encodeSegment(id)}`,
    feePayments: "/api/fees/payments",
    feePaymentById: (id: string | number) => `/api/fees/payments/${encodeSegment(id)}`,
    feeStudentLedger: (studentId: string | number) => `/api/fees/student/${encodeSegment(studentId)}/ledger`,
    feeStudentSummary: (studentId: string | number) => `/api/fees/student/${encodeSegment(studentId)}/summary`,
    feeReportsDaybook: "/api/fees/reports/daybook",
    feeReportsOutstanding: "/api/fees/reports/outstanding",
    feeReportsSummary: "/api/fees/reports/summary",
    feeReportsDefaulters: "/api/fees/reports/defaulters",
    feeReportsDashboard: "/api/fees/reports/dashboard",
    transportRoutes: "/api/transport/routes",
    transportRouteById: (id: string | number) => `/api/transport/routes/${encodeSegment(id)}`,
    transportRouteStops: (routeId: string | number) => `/api/transport/routes/${encodeSegment(routeId)}/stops`,
    transportRouteStopById: (routeId: string | number, stopId: string | number) => `/api/transport/routes/${encodeSegment(routeId)}/stops/${encodeSegment(stopId)}`,
    transportStudentAssignments: "/api/transport/student-assignments",
    transportStudentAssignmentById: (id: string | number) => `/api/transport/student-assignments/${encodeSegment(id)}`,
    transportGenerateFeeInstallments: "/api/transport/fee-installments/generate",
    assignments: "/api/assignments",
    assignmentById: (id: string | number) => `/api/assignments/${encodeSegment(id)}`,
    assignmentSubmissions: (id: string | number) => `/api/assignments/${encodeSegment(id)}/submissions`,
    assignmentSubmissionById: (id: string | number, studentId: string | number) => `/api/assignments/${encodeSegment(id)}/submissions/${encodeSegment(studentId)}`,
    announcements: "/api/announcements",
    announcementById: (id: string | number) => `/api/announcements/${encodeSegment(id)}`,
    circulars: "/api/circulars",
    circularById: (id: string | number) => `/api/circulars/${encodeSegment(id)}`,
    adminRoles: "/api/admin/roles",
    adminRolePermissions: "/api/admin/role-permissions",
    adminUsers: "/api/admin/users",
    adminUserRoles: (userId: string | number) => `/api/admin/users/${encodeSegment(userId)}/roles`,
    adminUserRoleById: (userId: string | number, roleId: string | number) => `/api/admin/users/${encodeSegment(userId)}/roles/${encodeSegment(roleId)}`,
    adminUsersLookup: "/api/admin/users/lookup",
    notificationTemplates: "/api/notification-templates",
    notificationTemplateById: (id: string | number) => `/api/notification-templates/${encodeSegment(id)}`,
    communicationFamilies: "/api/communication/recipients/families",
    communicationTeachers: "/api/communication/recipients/teachers",
    communicationStudents: "/api/communication/recipients/students",
    communicationCampaigns: "/api/communication/campaigns",
    communicationCampaignById: (id: string | number) => `/api/communication/campaigns/${encodeSegment(id)}`,
    teachers: "/api/teachers",
    teacherById: (teacherId: string | number) => `/api/teachers/${encodeSegment(teacherId)}`,
    teacherQualifications: (teacherId: string | number) => `/api/teachers/${encodeSegment(teacherId)}/qualifications`,
    teacherSubjects: (teacherId: string | number) => `/api/teachers/${encodeSegment(teacherId)}/subjects`,
    teacherSubjectById: (teacherId: string | number, assignmentId: string | number) => `/api/teachers/${encodeSegment(teacherId)}/subjects/${encodeSegment(assignmentId)}`,
    teacherSections: (teacherId: string | number) => `/api/teachers/${encodeSegment(teacherId)}/sections`,
    teacherAvailability: (teacherId: string | number) => `/api/teachers/${encodeSegment(teacherId)}/availability`,
    orgSchools: (orgId: string | number) => `/api/v1/orgs/${encodeSegment(orgId)}/schools`,
    orgSchoolById: (orgId: string | number, schoolId: string | number) =>
      `/api/v1/orgs/${encodeSegment(orgId)}/schools/${encodeSegment(schoolId)}`,
    orgAcademicYears: (orgId: string | number) => `/api/v1/orgs/${encodeSegment(orgId)}/acyears`,
    orgAcademicYearById: (orgId: string | number, academicYearId: string | number) =>
      `/api/v1/orgs/${encodeSegment(orgId)}/acyears/${encodeSegment(academicYearId)}`,
    orgSchoolOverview: (orgId: string | number, schoolId: string | number) =>
      `/api/v1/orgs/${encodeSegment(orgId)}/schools/${encodeSegment(schoolId)}/overview`,
    orgSchoolClasses: (orgId: string | number, schoolId: string | number) =>
      `/api/v1/orgs/${encodeSegment(orgId)}/schools/${encodeSegment(schoolId)}/classes`,
    orgSchoolClassById: (orgId: string | number, schoolId: string | number, classId: string | number) =>
      `/api/v1/orgs/${encodeSegment(orgId)}/schools/${encodeSegment(schoolId)}/classes/${encodeSegment(classId)}`,
    // HR module
    hrStaff: "/api/hr/staff",
    hrStaffById: (id: string | number) => `/api/hr/staff/${encodeSegment(id)}`,
    hrStaffPostings: (id: string | number) => `/api/hr/staff/${encodeSegment(id)}/postings`,
    hrStaffPostingById: (id: string | number, postingId: string | number) => `/api/hr/staff/${encodeSegment(id)}/postings/${encodeSegment(postingId)}`,
    hrStaffDocuments: (id: string | number) => `/api/hr/staff/${encodeSegment(id)}/documents`,
    hrStaffDocumentById: (id: string | number, docId: string | number) => `/api/hr/staff/${encodeSegment(id)}/documents/${encodeSegment(docId)}`,
    hrStaffSalary: (id: string | number) => `/api/hr/staff/${encodeSegment(id)}/salary`,
    hrStaffSalaryHistory: (id: string | number) => `/api/hr/staff/${encodeSegment(id)}/salary/history`,
    hrStaffPayslips: (id: string | number) => `/api/hr/staff/${encodeSegment(id)}/payslips`,
    hrSalaryComponents: "/api/hr/salary-components",
    hrSalaryComponentById: (id: string | number) => `/api/hr/salary-components/${encodeSegment(id)}`,
    hrSalaryComponentsInitialize: "/api/hr/salary-components/initialize",
    hrPayroll: "/api/hr/payroll",
    hrPayrollById: (id: string | number) => `/api/hr/payroll/${encodeSegment(id)}`,
    hrPayrollProcess: (id: string | number) => `/api/hr/payroll/${encodeSegment(id)}/process`,
    hrPayrollFinalize: (id: string | number) => `/api/hr/payroll/${encodeSegment(id)}/finalize`,
    hrPayrollCancel: (id: string | number) => `/api/hr/payroll/${encodeSegment(id)}/cancel`,
    hrPayslipDetail: (runId: string | number, staffId: string | number) => `/api/hr/payroll/${encodeSegment(runId)}/payslips/${encodeSegment(staffId)}`,
    hrExportBank: "/api/hr/export-bank",
    // Parent portal APIs
    parentMe: "/api/parent/me",
    parentDashboard: (studentId: string | number) => `/api/parent/dashboard?studentId=${encodeSegment(studentId)}`,
    parentMessages: "/api/parent/messages",
    parentLeaveRequests: "/api/parent/leave-requests",
    parentCirculars: "/api/parent/circulars",
    parentAnnouncements: "/api/parent/announcements",
    leaveTypes: (schoolId: string | number) => `/api/leave-types?schoolId=${encodeSegment(schoolId)}`,
    leaveRequests: "/api/leave-requests",
    leaveRequestById: (id: string | number) => `/api/leave-requests/${encodeSegment(id)}`,
    parentCircularAcknowledge: (id: string | number) => `/api/parent/circulars/${encodeSegment(id)}/acknowledge`,
    staffInboxCirculars: "/api/staff/inbox/circulars",
    staffInboxAnnouncements: "/api/staff/inbox/announcements",
    staffInboxCircularAcknowledge: (id: string | number) => `/api/staff/inbox/circulars/${encodeSegment(id)}/acknowledge`,
    // Mobile parent APIs
    parentStudentProfile: (studentId: string | number) => `/api/parent/student-profile?studentId=${encodeSegment(studentId)}`,
    parentAttendanceSummary: (studentId: string | number) => `/api/parent/attendance-summary?studentId=${encodeSegment(studentId)}`,
    parentAttendance: (studentId: string | number, month: number, year: number) => `/api/parent/attendance?studentId=${encodeSegment(studentId)}&month=${month}&year=${year}`,
    parentFeeSummary: (studentId: string | number) => `/api/parent/fee-summary?studentId=${encodeSegment(studentId)}`,
    parentFees: (studentId: string | number) => `/api/parent/fees?studentId=${encodeSegment(studentId)}`,
    parentCalendar: (studentId: string | number, month: number, year: number, schoolId: string | number) => `/api/parent/calendar?studentId=${encodeSegment(studentId)}&month=${month}&year=${year}&schoolId=${encodeSegment(schoolId)}`,
    parentNotifications: (studentId: string | number) => `/api/parent/notifications?studentId=${encodeSegment(studentId)}`,
    parentNotificationRead: (id: string | number) => `/api/parent/notifications/${encodeSegment(id)}/read`,
    parentReportCards: (studentId: string | number, academicYearId?: string | number) =>
      `/api/parent/report-cards?studentId=${encodeSegment(studentId)}${academicYearId ? `&academicYearId=${encodeSegment(academicYearId)}` : ""}`,
    parentDeviceToken: "/api/parent/device-token",
    parentMobileSession: "/api/parent/auth/mobile-session",
    // Assessment / Report Card v2
    evaluationPeriods: "/api/evaluation-periods",
    evaluationPeriodById: (id: string | number) => `/api/evaluation-periods/${encodeSegment(id)}`,
    assessmentComponents: "/api/assessment-components",
    assessmentComponentById: (id: string | number) => `/api/assessment-components/${encodeSegment(id)}`,
    studentAssessmentMarks: "/api/student-assessment-marks",
    studentAssessmentMarksSubmit: "/api/student-assessment-marks/submit",
    examCancellations: "/api/exam-cancellations",
    examCancellationById: (id: string | number) => `/api/exam-cancellations/${encodeSegment(id)}`,
    studentCompetencyRatings: "/api/student-competency-ratings",
    studentCompetencyRatingsSubmit: "/api/student-competency-ratings/submit",
    studentCompetencyRatingsPublish: "/api/student-competency-ratings/publish",
    competencyRatingCancellations: "/api/competency-rating-cancellations",
    competencyRatingCancellationById: (id: string | number) => `/api/competency-rating-cancellations/${encodeSegment(id)}`,
    developmentAreas: "/api/development-areas",
    developmentAreaById: (id: string | number) => `/api/development-areas/${encodeSegment(id)}`,
    competencySkills: "/api/competency-skills",
    competencySkillById: (id: string | number) => `/api/competency-skills/${encodeSegment(id)}`,
    competencyRatingScales: "/api/competency-rating-scales",
    competencyRatingScaleById: (id: string | number) => `/api/competency-rating-scales/${encodeSegment(id)}`,
    gradePeriodWeightages: "/api/grade-period-weightages",
    subjectCombinations: "/api/reports/subject-combinations",
    subjectCombinationsExport: "/api/reports/subject-combinations/export",
    marksProgress: "/api/reports/marks-progress",
    marksProgressExport: "/api/reports/marks-progress/export",
    classReportCard: "/api/reports/class-report-card",
    classAttendanceRegisterDay: "/api/reports/class-attendance-register/day",
    classAttendanceRegisterDayExport: "/api/reports/class-attendance-register/day/export",
    classAttendanceRegisterMonth: "/api/reports/class-attendance-register/month",
    classAttendanceRegisterMonthExport: "/api/reports/class-attendance-register/month/export",
    holidays: "/api/holidays",
    holidayById: (id: string | number) => `/api/holidays/${encodeSegment(id)}`,
    attendanceSettings: "/api/attendance-settings",
    academicYearGrades: "/api/academic-year-grades",
    academicYearGradeById: (id: string | number) => `/api/academic-year-grades/${encodeSegment(id)}`,
  },
  // Official legal pages (docs/refer/design/updatedterms-v1.2.md). Link only — never copy the text into the app.
  legal: {
    privacy: "https://www.solventc.com/privacy.html",
    termsOfService: "https://www.solventc.com/terms",
    termsOfUse: "https://www.solventc.com/terms-of-use",
  },
};

/** Attendance percentage below which a low-attendance warning badge is shown on the parent dashboard. */
export const PARENT_DASHBOARD_ATTENDANCE_THRESHOLD = 75;
